/**
 * The design foundation, rendered. This page exists so the brand guide can be checked against
 * running code — it is the sample used at gate G0 (PLAN.md 2.2 #29).
 */
import { useState } from "react";

import { Dialog } from "../components/Dialog";
import { EmptyState } from "../components/EmptyState";
import { AccentBlock, Badge, Button, Card, Field, Kbd, Num } from "../components/primitives";
import { SkeletonText } from "../components/Skeleton";
import { useToast } from "../components/toast";

function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <Card>
      <h2 className="nb-overline">{title}</h2>
      {note && <p className="mt-s4 max-w-[60ch] text-small text-muted">{note}</p>}
      <div className="mt-s5">{children}</div>
    </Card>
  );
}

export function DesignPage() {
  const showToast = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [archived, setArchived] = useState<string | null>(null);

  return (
    <>
      <Card hero>
        <p className="nb-overline">Designgrund</p>
        <h1 className="nb-title mt-s4 text-h2 text-ink">Komponenter och regler</h1>
        <p className="mt-s4 max-w-[60ch] text-body text-muted">
          Alla värden läses som tokens ur{" "}
          <span className="nb-num">Visualguideline/tokens/nordiska.css</span>. Inget färgvärde är
          skrivet i komponentkoden.
        </p>
      </Card>

      <Section
        title="Knappar"
        note="Glöden är enbart CTA — en orange knapp per vy. Text på Glöden är Djupet, aldrig vit. Behöver en vy två viktiga knappar är den andra en primär, inte en CTA."
      >
        <div className="flex flex-wrap items-center gap-s4">
          <Button variant="cta">Acceptera förslag</Button>
          <Button variant="primary">Spara</Button>
          <Button>Avbryt</Button>
          <Button variant="ghost">Mer</Button>
          <Button variant="danger">Radera</Button>
          <Button disabled>Inaktiv</Button>
        </div>
      </Section>

      <Section
        title="Fält"
        note="Etiketten står ovanför fältet, aldrig som platshållare. Numeriska värden sätts i IBM Plex Mono med tabular-nums."
      >
        <div className="grid gap-s5 md:grid-cols-2">
          <Field label="Projektnamn" placeholder="Kv. Ankaret" />
          <Field label="Budget" numeric defaultValue="1 250 000" help="Kronor, exklusive moms" />
          <Field label="Avtalsreferens" error="Referensen finns inte i registret" defaultValue="ÄTA-9" />
          <Field label="Ansvarig" defaultValue="Anna Karlsson" disabled />
        </div>
      </Section>

      <Section title="Status och siffror" note="Statusfärgerna är gemensamma för hela företaget.">
        <div className="flex flex-wrap items-center gap-s5">
          <Badge status="pass">Godkänd</Badge>
          <Badge status="warn">Avvaktar</Badge>
          <Badge status="fail">Avvisad</Badge>
          <span className="text-body text-ink">
            Flöde <Num>367,5</Num> l/min · Upparbetat <Num>84,2</Num> %
          </span>
        </div>
      </Section>

      <Section
        title="Framhävt innehåll"
        note="Den cyanfärgade vänsterlisten är varumärkets signaturmarkering."
      >
        <AccentBlock>
          <p className="text-body text-ink">
            Beslut fattat 2026-08-04: leveranstiden förlängs med två veckor mot avtalets punkt 7.2.
          </p>
        </AccentBlock>
      </Section>

      <Section
        title="Ångra i stället för att fråga"
        note="Reversibla handlingar sker direkt och erbjuder Ångra. Bekräftelsedialog används bara för det oåterkalleliga — annars lär man sig att alltid klicka ja."
      >
        <div className="flex flex-wrap items-center gap-s4">
          <Button
            onClick={() => {
              setArchived("Kv. Ankaret");
              showToast({
                message: "Posten arkiverades.",
                undo: () => {
                  setArchived(null);
                  showToast({ message: "Arkiveringen ångrades.", tone: "pass" });
                },
              });
            }}
          >
            Arkivera post
          </Button>
          <Button variant="danger" onClick={() => setDialogOpen(true)}>
            Radera permanent
          </Button>
          <span className="text-small text-muted">
            {archived ? `Arkiverad: ${archived}` : "Inget arkiverat"}
          </span>
        </div>
      </Section>

      <Section title="Laddning" note="Skelett i innehållets form, aldrig en spinner över hela sidan.">
        <SkeletonText lines={3} />
      </Section>

      <Section title="Tomt läge" note="Varje lista har ett designat tomt läge som säger vad man gör härnäst.">
        <EmptyState
          title="Inga förslag att granska"
          description="När pipelinen börjar tolka mail och möten hamnar förslagen här för din granskning."
          action={<Button variant="primary">Läs mer om acceptanskön</Button>}
        />
      </Section>

      <Section title="Tangentbord" note="Kommandopaletten är produktens primära navigering.">
        <p className="flex flex-wrap items-center gap-s3 text-body text-ink">
          Öppna paletten med <Kbd>Ctrl + K</Kbd> och visa alla genvägar med <Kbd>Skift + ?</Kbd>.
        </p>
      </Section>

      <Dialog
        open={dialogOpen}
        title="Radera permanent?"
        subtitle="Går inte att ångra"
        onClose={() => setDialogOpen(false)}
        footer={
          <>
            <Button onClick={() => setDialogOpen(false)}>Avbryt</Button>
            <Button
              variant="danger"
              onClick={() => {
                setDialogOpen(false);
                showToast({ message: "Posten raderades permanent.", tone: "fail" });
              }}
            >
              Radera
            </Button>
          </>
        }
      >
        <p className="text-body">
          Posten och dess revisioner tas bort. Det här är exempel på när en dialog är rätt: åtgärden
          går inte att ångra.
        </p>
      </Dialog>
    </>
  );
}
